import React, { useCallback, useEffect, useState } from 'react';
import { Card, Dropdown, DropdownButton, Table } from 'react-bootstrap';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { ContractService } from '../ContractService';
import { formatDate, getCorrectEnums } from '../../../utils/Functions';
import { Contract, IContractInvoiceSchedule } from '../../../model/contract/Contract';
import { priceSplitter } from '../../../utils/helpers/NumberFormater';
import generalSlice from '../../../store/general/GeneralSlice';
import { useDispatch } from 'react-redux';
import { downloadFileRequest } from '../../../api/api.service';
import { DOWNLOAD_INVOICE } from '../../../utils/Endpoints';
import EmptyState from '../../../shared/components/EmptyState';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';

const SCHEDULE_COLUMNS: ITableColumn[] = [
    { label: 'Position' },
    { label: 'Schedule date' },
    { label: 'Status' },
    { label: 'Price' },
    { label: 'Invoice date' },
    { label: 'Is upload allowed?' },
    { label: '' },
];

const SKELETON_ROWS = 5;

export function ContractScheduleEdit() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [schedule, setSchedule] = useState<IContractInvoiceSchedule[]>([]);
    const [contract, setContract] = useState<Contract>({} as Contract);
    const [totalSchedules, setTotalSchedules] = useState(0);
    const [dataLoading, setDataLoading] = useState(true);

    const requestParams = {
        page: 1,
        perPage: 0,
    };

    const loadSchedule = useCallback(() => {
        setDataLoading(true);
        ContractService.getContractsSchedule(Number(id), requestParams)
            .then(({ data, count, info }) => {
                setSchedule(data);
                setContract(info);
                setTotalSchedules(count);
            })
            .catch(ErrorToast)
            .finally(() => setDataLoading(false));
    }, [id]);

    useEffect(() => {
        loadSchedule();
    }, [loadSchedule]);

    function allowInvoiceUpload(id: number) {
        ContractService.allowUpload(id)
            .then(({ data }) => {
                setSchedule(data);
                SuccessToast('Successfully allowed');
                dispatch(
                    generalSlice.actions.setConfirmModal({
                        show: false,
                    })
                );
            })
            .catch(err => ErrorToast(err));
    }

    const doDownload = async (invoiceId: number) => {
        try {
            downloadFileRequest(`${DOWNLOAD_INVOICE}/${invoiceId}`, 'invoice', null, null, setIsLoading);
        } catch (error) {
            ErrorToast(error);
        }
    };

    return (
        <>
            <Card className="mb-5 p-4 mt-3">
                <div className="row ml-1">
                    <div className="col-auto">
                        <strong>Contract ID:</strong> {contract.id}
                    </div>
                    <div className="col-auto">
                        <strong>Influencer payout:</strong> {priceSplitter(contract?.price)} €
                    </div>
                    {contract.hasInfluencerFee && (
                        <div className="col-auto">
                            <strong>Influencer fee:</strong> {contract?.influencerFeePercentage}%
                        </div>
                    )}
                    <div className="col-auto">
                        <strong>Influencer:</strong> ({contract?.user?.id}) {contract?.user?.displayName}
                    </div>
                    <div className="col-auto">
                        <strong>Payment period:</strong> {getCorrectEnums(contract?.invoicePeriod ?? 'N/A')}
                    </div>
                    <div className="col-auto">
                        <strong>Payment duration:</strong> {getCorrectEnums(contract?.invoiceDuration ?? 'N/A')}
                    </div>
                    <div className="col-auto">
                        <strong>Campaign:</strong> ({contract?.campaign?.id}) {contract?.campaign?.title}
                    </div>
                    <div className="col-auto">
                        <strong>Company:</strong> ({contract?.campaign?.company?.id}){' '}
                        {contract?.campaign?.company?.displayName}
                    </div>
                </div>
            </Card>
            <Card className="mb-5 p-4">
                {dataLoading ? (
                    <TableSkeleton columns={SCHEDULE_COLUMNS} rows={SKELETON_ROWS} />
                ) : (
                    <>
                        <Table hover responsive>
                            <thead>
                                <tr>
                                    {SCHEDULE_COLUMNS.map(column => (
                                        <th key={column.label} className={column.className}>
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {schedule.map(s => (
                                    <tr key={s.id}>
                                        <td>
                                            {s.sequenceNumber}/{totalSchedules}
                                        </td>
                                        <td>{formatDate(s?.dueDate)}</td>
                                        <td>{getCorrectEnums(s.status ?? '')}</td>
                                        <td>{s.price} €</td>
                                        <td>{formatDate(s?.invoice?.date)}</td>
                                        <td>
                                            {s?.isAllowed ? (
                                                <span className="word-break text-primary">
                                                    <FaCheck />
                                                </span>
                                            ) : (
                                                <span className="word-break text-danger">
                                                    <FaTimes />
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <DropdownButton
                                                title="Actions"
                                                id="dropdown-menu-align-right"
                                                disabled={s?.isAllowed && !s?.invoice?.id}
                                            >
                                                {!s.isAllowed && (
                                                    <Dropdown.Item
                                                        onClick={() => {
                                                            dispatch(
                                                                generalSlice.actions.setConfirmModal({
                                                                    show: true,
                                                                    title: 'Invoice upload allow',
                                                                    contentBody: (
                                                                        <div>
                                                                            Are you sure you want to allow uploading of
                                                                            invoice planned on {formatDate(s.dueDate)}?
                                                                        </div>
                                                                    ),
                                                                    action: () => allowInvoiceUpload(s.id ?? 0),
                                                                })
                                                            );
                                                        }}
                                                        eventKey="1"
                                                    >
                                                        Allow uploading
                                                    </Dropdown.Item>
                                                )}
                                                {s?.invoice?.id && (
                                                    <Dropdown.Item
                                                        onClick={() => doDownload(s?.invoice?.id)}
                                                        disabled={isLoading}
                                                        eventKey="2"
                                                    >
                                                        Download invoice
                                                    </Dropdown.Item>
                                                )}
                                            </DropdownButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        {schedule.length === 0 && <EmptyState classHolder="shadow-none" />}
                    </>
                )}
            </Card>
        </>
    );
}
